import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { sellersApi, favoritesApi } from '../../services/apiService';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ChatModal from '../../components/ChatModal/ChatModal';
import LoginModal from '../../components/LoginModal/LoginModal';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import ProductCardFigma from '../../components/ProductCard/ProductCardFigma';
import Pagination from '../../components/Pagination/Pagination';
import { useSubscription } from '../../context/SubscriptionContext';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { getPlanConfig, shouldShowVerifiedBadge, canSendInquiry } from '../../utils/membershipUtils';
import './CompanyProfilePage.css';

// Sample products for display when no real products exist
const sampleProducts = [
  {
    id: 1,
    name: 'Construction of 15 story commercial office building with modern amenities and parking facilities.',
    image: '/uploads/builder-images/328a0f6dd6e767b8279791bf791abd79bf7b4c61_w226.png',
    priceMin: 2.5,
    priceMax: 3.2,
    unit: 'Tons',
    moq: 15,
    category: 'Aggregates'
  },
  {
    id: 2,
    name: 'High-quality Portland cement for residential and commercial construction projects.',
    image: '/uploads/builder-images/328a0f6dd6e767b8279791bf791abd79bf7b4c61_w226.png',
    priceMin: 3.0,
    priceMax: 4.5,
    unit: 'Tons',
    moq: 10,
    category: 'Cement'
  },
  {
    id: 3,
    name: 'Premium grade steel reinforcement bars for heavy-duty construction applications.',
    image: '/uploads/builder-images/328a0f6dd6e767b8279791bf791abd79bf7b4c61_w226.png',
    priceMin: 1.8,
    priceMax: 2.5,
    unit: 'Tons',
    moq: 20,
    category: 'Steel'
  },
  {
    id: 4,
    name: 'Industrial-grade ready-mix concrete for large scale infrastructure projects.',
    image: '/uploads/builder-images/328a0f6dd6e767b8279791bf791abd79bf7b4c61_w226.png',
    priceMin: 2.0,
    priceMax: 3.0,
    unit: 'Cubic Meters',
    moq: 25,
    category: 'Concrete'
  },
  {
    id: 5,
    name: 'Specialized building materials for eco-friendly sustainable construction.',
    image: '/uploads/builder-images/328a0f6dd6e767b8279791bf791abd79bf7b4c61_w226.png',
    priceMin: 4.0,
    priceMax: 5.5,
    unit: 'Units',
    moq: 50,
    category: 'Blocks'
  }
];

const productCategories = [
  'Building Materials',
  'Aggregates',
  'Blocks',
  'Bricks',
  'Cement',
  'Concrete',
  'Sand',
  'Steel',
  'Timber'
];

const CompanyProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, planName, planDisplayName, canViewContactsForRegion, getFeature, loading: subscriptionLoading } = useSubscription();
  const { isAuthenticated, user } = useAuth();
  const { formatPriceRange } = useCurrency();
  const [activeTab, setActiveTab] = useState('home');
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkToggling, setBookmarkToggling] = useState(false);
  const [bookmarkToast, setBookmarkToast] = useState(null);
  const bookmarkToastTimer = useRef(null);

  const showBookmarkToast = (msg, type = 'success') => {
    setBookmarkToast({ msg, type });
    if (bookmarkToastTimer.current) clearTimeout(bookmarkToastTimer.current);
    bookmarkToastTimer.current = setTimeout(() => setBookmarkToast(null), 2500);
  };

  useEffect(() => () => { if (bookmarkToastTimer.current) clearTimeout(bookmarkToastTimer.current); }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [showMobile, setShowMobile] = useState(false);
  const [showProductFilter, setShowProductFilter] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [productPage, setProductPage] = useState(1);
  const PRODUCTS_PER_PAGE = 16;
  const filterRef = useRef(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCertificatesDetails, setShowCertificatesDetails] = useState(false);
  const [showReviewsSection, setShowReviewsSection] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [upgradeMessage, setUpgradeMessage] = useState('');
  
  // Inquiry form state
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [inquiryEmail, setInquiryEmail] = useState('');
  const [inquiryLoading, setInquiryLoading] = useState(false);

  // Carousel scroll states
  const [highlightScrollIndex, setHighlightScrollIndex] = useState(0);
  const [latestScrollIndex, setLatestScrollIndex] = useState(0);
  const [categoryScrollIndex, setCategoryScrollIndex] = useState(0);
  const [snapshotScrollIndex, setSnapshotScrollIndex] = useState(0);

  // Carousel refs for native scroll
  const highlightCarouselRef = useRef(null);
  const latestCarouselRef = useRef(null);
  const categoryCarouselRef = useRef(null);
  const snapshotCarouselRef = useRef(null);

  // Handle inquiry form submission
  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    
    // Check if user has Basic plan - they cannot send inquiries
    // Admin bypass - admin can do everything
    const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
    if (!isAdminUser && !canSendInquiry(planName)) {
      alert('Basic plan users cannot send inquiries. Please upgrade to GCC or Global plan to access this feature.');
      return;
    }
    
    if (!inquiryMessage.trim() || !inquiryEmail.trim()) {
      alert('Please fill in both email and message');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inquiryEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    setInquiryLoading(true);
    try {
      const response = await fetch(`${'/api'}/contact/supplier-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: 'Website Visitor',
          email: inquiryEmail,
          subject: `Inquiry about ${companyData?.name || 'your company'}`,
          message: inquiryMessage,
          supplierId: companyData?.id,
          supplierName: companyData?.name,
          supplierEmail: companyData?.email
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Inquiry sent successfully!');
        setInquiryMessage('');
        setInquiryEmail('');
      } else {
        alert(data.error || 'Failed to send inquiry. Please try again.');
      }
    } catch (err) {
      console.error('Error sending inquiry:', err);
      alert('Failed to send inquiry. Please try again.');
    } finally {
      setInquiryLoading(false);
    }
  };

  // Handle tab based on URL path and query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const path = location.pathname;
    
    if (tabParam === 'reviews') {
      setActiveTab('reviews');
      setShowReviewsSection(true);
    } else if (path.includes('/products')) {
      setActiveTab('product');
      setShowReviewsSection(false);
    } else if (path.includes('/contact')) {
      setActiveTab('contact');
      setShowReviewsSection(false);
    } else if (path.includes('/profile')) {
      setActiveTab('company-profile');
      setShowReviewsSection(false);
    } else {
      setActiveTab('home');
      setShowReviewsSection(false);
    }
  }, [location.pathname, location.search]);

  // Close filter dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowProductFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch seller data
  useEffect(() => {
    const fetchSellerData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setUpgradeRequired(false);
        const data = await sellersApi.getById(id);
        setCompanyData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching seller:', err);
        // Check if this is a 403 upgrade required error
        if (err.response?.status === 403 && err.response?.data?.upgradeRequired) {
          setUpgradeRequired(true);
          setError(err.response?.data?.message || 'Upgrade required to view company profiles');
          setShowUpgradeModal(true);
        } else if (err.message?.includes('upgrade') || err.message?.includes('Upgrade')) {
          setUpgradeRequired(true);
          setError('Upgrade your membership to view company profiles');
          setShowUpgradeModal(true);
        } else {
          setError('Failed to load company profile');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSellerData();
  }, [id]);

  // Check and sync favourite status for this seller
  useEffect(() => {
    const checkFavourite = async () => {
      if (user?.id && companyData?.id) {
        try {
          const response = await favoritesApi.checkFavorite(user.id, companyData.id);
          setIsBookmarked(response.isFavorited);
        } catch (err) {
          console.error('Error checking seller favourite:', err);
        }
      }
    };
    checkFavourite();
  }, [user?.id, companyData?.id]);

  // Toggle seller bookmark / favourite
  const handleToggleBookmark = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!companyData?.id || bookmarkToggling) return;

    // Optimistic update
    const prev = isBookmarked;
    setIsBookmarked(!prev);
    setBookmarkToggling(true);

    try {
      const response = await favoritesApi.toggle(user.id, companyData.id);
      setIsBookmarked(response.isFavorited);
      showBookmarkToast(response.isFavorited ? 'Added to favourites' : 'Removed from favourites');
    } catch (err) {
      setIsBookmarked(prev);
      showBookmarkToast('Could not update favourites. Try again.', 'error');
      console.error('Error toggling seller favourite:', err);
    } finally {
      setBookmarkToggling(false);
    }
  };

  // Check if logged-in user can view phone numbers based on their plan
  // Basic: no contacts visible, GCC Business: only GCC contacts, Global Enterprise: all contacts
  const hasContactPlanPermission = hasPermission('canViewBuyerContacts');
  
  // Check if user can view this specific supplier's contact based on LOGGED-IN USER's plan and region
  // Contact visibility is controlled by the VIEWER's plan, not the seller's plan:
  // Basic viewer: NO contacts visible
  // GCC Business viewer: ONLY GCC country supplier contacts visible
  // Global Enterprise viewer: ALL contacts visible
  const canViewSupplierContact = () => {
    // Admin bypass - admin can see everything
    const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
    if (isAdminUser) return true;
    
    // First check if user has the base permission (GCC Business or Global Enterprise)
    if (!hasContactPlanPermission) {
      return false; // Basic plan cannot view any contacts
    }
    
    const supplierCountry = companyData?.country || '';
    const contactRegion = getFeature ? getFeature('buyerContactRegion') : null;
    
    // If region is 'all' or 'global', can view any contact (Global Enterprise)
    if (contactRegion === 'all' || contactRegion === 'global') {
      return true;
    }
    
    // If region is 'gcc', check if supplier is from GCC country (GCC Business)
    if (contactRegion === 'gcc') {
      return canViewContactsForRegion ? canViewContactsForRegion(supplierCountry) : false;
    }
    
    return true;
  };
  
  // Get phone display based on user's plan AND supplier's country (GCC filtering)
  // Basic: no contacts, GCC Business: only GCC suppliers, Global Enterprise: all suppliers
  const getPhoneDisplay = () => {
    const actualPhone = companyData?.phoneNumber || companyData?.phone || companyData?.mobile;
    
    if (!actualPhone) return 'N/A';
    
    // Use canViewSupplierContact for consistent logic
    if (canViewSupplierContact()) {
      return actualPhone;
    }
    return '***-***-****';  // Masked for Basic users or non-GCC suppliers for GCC Business
  };
  
  // Get email display based on user's plan AND supplier's country (GCC filtering)
  // Basic: masked, GCC Business: only GCC suppliers, Global Enterprise: all suppliers
  const getEmailDisplay = () => {
    const actualEmail = companyData?.email;
    
    if (!actualEmail) return 'N/A';
    
    // Use canViewSupplierContact for consistent logic
    if (canViewSupplierContact()) {
      return actualEmail;
    }
    return '****@****.com';  // Masked for Basic users or non-GCC suppliers for GCC Business
  };

  const tabs = [
    { id: 'home', label: 'Home' },
    { id: 'company-profile', label: 'Company Profile' },
    { id: 'product', label: 'Product' },
    { id: 'contact', label: 'Contact' }
  ];

  // Parse JSON fields safely
  const parseJsonField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      return JSON.parse(field);
    } catch {
      return [];
    }
  };

  // Strip HTML tags and get clean text
  const stripHtml = (html) => {
    if (!html) return '';
    // Remove HTML tags
    const text = html.replace(/<[^>]*>/g, '');
    // Decode HTML entities
    const decoded = text.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    return decoded.trim();
  };

  // Get membership type display
  const getMembershipDisplay = (plan) => {
    if (!plan) return 'Basic';
    return plan.charAt(0).toUpperCase() + plan.slice(1).toLowerCase();
  };

  // Get verification display
  const getVerificationDisplay = (level) => {
    if (!level || level === 'pending') return null;
    return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
  };

  // Handle Home tab - switch to home tab
  const handleGoHome = () => {
    setActiveTab('home');
    setShowReviewsSection(false);
    window.history.replaceState(null, '', `/supplier/${id}`);
  };

  // Handle Inquire Now button - switch to contact tab
  const handleInquireNow = () => {
    setActiveTab('contact');
    setShowReviewsSection(false);
    window.history.replaceState(null, '', `/supplier/${id}/contact`);
  };

  // Handle View Mobile button - check if LOGGED-IN USER has permission to view contacts
  // Based on plan and supplier's country:
  // Basic: ❌ cannot view any mobile numbers
  // GCC Business: ✅ can view mobile numbers ONLY for GCC region suppliers (UAE, Saudi Arabia, Qatar, Kuwait, Bahrain, Oman)
  // Global Enterprise: ✅ can view all mobile numbers worldwide
  const handleViewMobile = () => {
    // Check if user is logged in first
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    // Wait for subscription to load
    if (subscriptionLoading) return;
    
    const supplierCountry = companyData?.country || '';
    const contactRegion = getFeature ? getFeature('buyerContactRegion') : null;
    const hasContactPermission = hasPermission('canViewBuyerContacts');
    
    // Admin bypass
    const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
    if (isAdminUser) {
      setShowMobile(!showMobile);
      return;
    }
    
    // Basic plan - no access at all
    if (!hasContactPermission) {
      setUpgradeFeature('View Mobile Number');
      setUpgradeMessage('Upgrade to GCC Business to view supplier mobile numbers in GCC region, or Global Enterprise for worldwide access.');
      setShowUpgradeModal(true);
      return;
    }
    
    // GCC Business plan - check if supplier is from GCC country
    if (contactRegion === 'gcc') {
      const canViewThisSupplier = canViewContactsForRegion ? canViewContactsForRegion(supplierCountry) : false;
      
      if (!canViewThisSupplier) {
        // GCC user trying to view non-GCC supplier
        setUpgradeFeature('View Mobile Number');
        setUpgradeMessage('This supplier is outside the GCC region. Upgrade to Global Enterprise to view mobile numbers for all suppliers worldwide.');
        setShowUpgradeModal(true);
        return;
      }
    }
    
    // Has permission - toggle mobile display
    setShowMobile(!showMobile);
  };

  // Handle Chat Now button - open chat modal (all plans can view messages, only GCC+ can reply)
  const handleChatNow = () => {
    // Check if user is logged in first
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    // Wait for subscription to load before checking permission
    if (subscriptionLoading) return;
    
    // All plans can open chat to view messages (Basic can view but not reply)
    setIsChatModalOpen(true);
  };

  // Handle See More certificates - toggle certificate details
  const handleSeeMoreCertificates = () => {
    setShowCertificatesDetails(!showCertificatesDetails);
  };

  // Build sidebar categories dynamically from actual product data
  const sidebarCategories = React.useMemo(() => {
    const products = (companyData?.products || []).filter(p => p.status === 'published');
    if (products.length === 0) return [];
    
    // Group products by category name
    const categoryMap = {};
    products.forEach(p => {
      const catName = p.category?.name || p.category || 'Uncategorized';
      const catId = String(p.category?.id || catName).toLowerCase().replace(/\s+/g, '-');
      if (!categoryMap[catId]) {
        categoryMap[catId] = { id: catId, name: catName, productCount: 0, subcategories: new Set() };
      }
      categoryMap[catId].productCount++;
      // If product has a subcategory name, add it
      if (p.subcategory?.name) {
        categoryMap[catId].subcategories.add(p.subcategory.name);
      }
    });
    
    return Object.values(categoryMap).map(cat => ({
      id: cat.id,
      name: cat.name,
      productCount: cat.productCount,
      subcategories: cat.subcategories.size > 0 
        ? Array.from(cat.subcategories).map(name => ({ name })) 
        : null
    }));
  }, [companyData?.products]);

  // Auto-expand first category on load
  React.useEffect(() => {
    if (sidebarCategories.length > 0 && Object.keys(expandedCategories).length === 0) {
      setExpandedCategories({ [sidebarCategories[0].id]: true });
    }
  }, [sidebarCategories]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleCategorySelect = (categoryName) => {
    setSelectedCategory(prev => prev === categoryName ? null : categoryName);
    setSelectedSubcategory(null);
    setProductPage(1);
  };

  const handleSubcategorySelect = (subcategoryName) => {
    setSelectedSubcategory(prev => prev === subcategoryName ? null : subcategoryName);
    setProductPage(1);
  };

  // Generic carousel scroll function
  const scrollCarousel = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 300; // Scroll by card width
      const newScrollLeft = ref.current.scrollLeft + (direction === 'next' ? scrollAmount : -scrollAmount);
      ref.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  const handleHighlightPrev = () => scrollCarousel(highlightCarouselRef, 'prev');
  const handleHighlightNext = () => scrollCarousel(highlightCarouselRef, 'next');

  const handleLatestPrev = () => scrollCarousel(latestCarouselRef, 'prev');
  const handleLatestNext = () => scrollCarousel(latestCarouselRef, 'next');

  const handleCategoryPrev = () => scrollCarousel(categoryCarouselRef, 'prev');
  const handleCategoryNext = () => scrollCarousel(categoryCarouselRef, 'next');

  const handleSnapshotPrev = () => scrollCarousel(snapshotCarouselRef, 'prev');
  const handleSnapshotNext = () => scrollCarousel(snapshotCarouselRef, 'next');

  // Handle brochure download - force download instead of opening in new tab
  const handleDownloadBrochure = async (brochure) => {
    if (brochure) {
      try {
        // Get the actual URL - could be regular URL or base64 data URL
        const url = typeof brochure === 'string' ? brochure : brochure.url;
        const fileName = typeof brochure === 'object' && brochure.name ? brochure.name : 'brochure';
        
        // Check if it's a base64 data URL
        if (url && url.startsWith('data:')) {
          // Extract the data and mime type from base64 URL
          const matches = url.match(/^data:([^;]+);base64,(.+)$/);
          if (matches) {
            const mimeType = matches[1];
            const base64Data = matches[2];
            
            // Convert base64 to blob
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });
            
            // Determine file extension from mime type
            let extension = '.pdf';
            if (mimeType.includes('word') || mimeType.includes('msword') || mimeType.includes('openxmlformats')) {
              extension = '.docx';
            } else if (mimeType.includes('pdf')) {
              extension = '.pdf';
            }
            
            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName.includes('.') ? fileName : fileName + extension;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
          }
        } else if (url) {
          // Regular URL - fetch and download
          const response = await fetch(url);
          const blob = await response.blob();
          
          // Create download link
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = fileName.includes('.') ? fileName : fileName + '.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        }
      } catch (error) {
        console.error('Error downloading brochure:', error);
        alert('Error downloading file. Please try again.');
      }
    } else {
      alert('No brochure available for this company.');
    }
  };

  // Get contact person for chat
  const getContactPerson = () => {
    if (!companyData) return null;
    return {
      name: companyData.contactPerson || companyData.name || 'Company Representative',
      role: companyData.contactRole || 'Sales Manager',
      avatar: companyData.contactAvatar || companyData.logo || '/uploads/builder-images/561da124ca9a57c1461a9da91c830f61842922c8_w104.png'
    };
  };

  if (loading) {
    return (
      <div className="company-profile-page">
        <Header />
        <main className="company-profile-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading company profile...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !companyData) {
    return (
      <div className="company-profile-page">
        <Header />
        <main className="company-profile-content">
          <div className="error-container">
            {upgradeRequired ? (
              <>
                <div className="upgrade-required-icon">🔒</div>
                <h2>Premium Feature</h2>
                <p>Viewing company profiles requires a paid membership plan.</p>
                <p className="upgrade-hint">Upgrade to GCC Business or Global Enterprise to unlock this feature.</p>
                <div className="upgrade-actions">
                  <Link to="/membership" className="upgrade-btn">View Plans</Link>
                  <Link to="/suppliers" className="back-link">Back to Suppliers</Link>
                </div>
              </>
            ) : (
              <>
                <p>{error || 'Company not found'}</p>
                <Link to="/suppliers" className="back-link">Back to Suppliers</Link>
              </>
            )}
          </div>
        </main>
        <Footer />
        {/* Upgrade Modal */}
        <UpgradePromptModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="Company Profiles"
          currentPlan="Basic"
          requiredPlan="GCC Business"
        />
      </div>
    );
  }

  const companyNotesList = parseJsonField(companyData.companyNotes);
  const snapshots = parseJsonField(companyData.snapshots);
  const brochuresList = parseJsonField(companyData.brochures);

  return (
    <div className="company-profile-page">
      <Header />
      
      {/* Breadcrumb - above supplier header */}
      <div className="breadcrumb-bar">
        <div className="breadcrumb">
          <Link to="/" className="breadcrumb-link">Home</Link>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M5 13L9 9L5 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <Link to="/suppliers" className="breadcrumb-link">Suppliers</Link>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M5 13L9 9L5 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="breadcrumb-current">
            {companyData?.name || 'Supplier Profile'}
          </span>
        </div>
      </div>

      <main className="company-profile-content">
        {/* Company Header Section */}
        <div className="company-header-section">
          <div className="company-header-container">
            <div className="company-header-left">
              <div className="company-logo-container">
                {companyData.logo ? (
                  <img 
                    src={companyData.logo} 
                    alt={companyData.name} 
                    className="company-logo"
                  />
                ) : (
                  <div className="company-logo-placeholder">
                    {companyData.name?.charAt(0) || 'C'}
                  </div>
                )}
              </div>
              <div className="company-header-info">
                <h1 className="company-name">{companyData.name}</h1>
                {/* Meta Row - All badges, country, business type in one horizontal row */}
                <div className="company-meta-row">
                  {/* Membership Plan Badge */}
                  {(() => {
                    const planConfig = getPlanConfig(companyData.membershipPlan);
                    return (
                      <div className="global-badge" style={{ borderColor: planConfig.color }}>
                        <img src={planConfig.icon} alt={planConfig.label} style={{ width: '22px', height: '22px' }} />
                        <span className="global-text" style={{ color: planConfig.textColor || '#6D6D6D' }}>{planConfig.label}</span>
                      </div>
                    );
                  })()}
                  
                  {/* Verified Badge - only for GCC and Global plans (not Basic) */}
                  {shouldShowVerifiedBadge(companyData.membershipPlan) && (
                    <div className="verified-badge">
                      <svg width="22" height="21" viewBox="0 0 22 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 10.5C22 11.4323 20.7329 12.1871 20.5034 13.0472C20.2653 13.9377 20.9782 15.2221 20.528 16.0015C20.0705 16.7923 18.597 16.814 17.9562 17.4562C17.314 18.0984 17.2923 19.5705 16.5014 20.028C15.7221 20.4782 14.4377 19.7653 13.5472 20.0034C12.6871 20.2329 11.9337 21.5 11 21.5C10.0663 21.5 9.31291 20.2329 8.45276 20.0034C7.56231 19.7653 6.27788 20.4782 5.49856 20.028C4.70769 19.5705 4.68605 18.097 4.04382 17.4562C3.4016 16.8154 1.92954 16.7923 1.47205 16.0015C1.02177 15.2221 1.73472 13.9377 1.49659 13.0472C1.26712 12.1871 0 11.4338 0 10.5C0 9.56627 1.26712 8.81292 1.49659 7.95278C1.73472 7.06233 1.02177 5.77789 1.47205 4.99857C1.92954 4.2077 3.40305 4.18605 4.04382 3.54383C4.68605 2.90161 4.70769 1.42955 5.49856 0.972058C6.27788 0.521782 7.56231 1.23472 8.45276 0.99659C9.31291 0.767122 10.0663 -0.5 11 -0.5C11.9337 -0.5 12.6871 0.767122 13.5472 0.99659C14.4377 1.23472 15.7221 0.521782 16.5014 0.972058C17.2923 1.42955 17.314 2.90305 17.9562 3.54383C18.5984 4.18461 20.0705 4.2077 20.528 4.99857C20.9782 5.77789 20.2653 7.06233 20.5034 7.95278C20.7329 8.81292 22 9.56627 22 10.5Z" fill="#29ABE2"/>
                        <path d="M16.588 7.1017C16.1233 6.637 15.37 6.637 14.9052 7.1017L10.0316 11.9754L8.00823 9.95201C7.54352 9.4873 6.79017 9.4873 6.32546 9.95201C5.86075 10.4167 5.86075 11.1701 6.32546 11.6348L9.14835 14.4577C9.39225 14.7016 9.71408 14.817 10.033 14.8055C10.352 14.817 10.6738 14.7016 10.9177 14.4577L16.5894 8.78591C17.0541 8.3212 17.0541 7.56786 16.5894 7.10315L16.588 7.1017Z" fill="white"/>
                      </svg>
                      <span className="verified-text">Verified</span>
                    </div>
                  )}
                  
                  {/* Country Section */}
                  <div className="country-section">
                    <div className="country-flag-container">
                      <img 
                        src={companyData.countryFlag || 'https://flagcdn.com/w40/us.png'} 
                        alt={companyData.country} 
                        className="country-flag"
                      />
                    </div>
                    <span className="country-text">
                      <span className="country-name">{companyData.country || 'United State'}</span>
                      <span className="separator"> / </span>
                      <span className="years-text">{String(companyData.memberYears || 1).padStart(2, '0')} Year</span>
                    </span>
                  </div>
                  
                  {/* Business Type Section */}
                  <div className="business-type-section">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.5 5H12V6H14.5V9H11V10H12.5V11.071C12.0287 11.1927 11.6179 11.4821 11.3447 11.885C11.0715 12.2879 10.9546 12.7766 11.0159 13.2595C11.0773 13.7424 11.3126 14.1864 11.6779 14.5082C12.0431 14.83 12.5132 15.0075 13 15.0075C13.4868 15.0075 13.9569 14.83 14.3221 14.5082C14.6874 14.1864 14.9227 13.7424 14.9841 13.2595C15.0454 12.7766 14.9285 12.2879 14.6553 11.885C14.3821 11.4821 13.9713 11.1927 13.5 11.071V10H14.5C14.7651 9.9996 15.0192 9.89412 15.2067 9.70667C15.3941 9.51922 15.4996 9.26509 15.5 9V6C15.5 5.73478 15.3946 5.48043 15.2071 5.29289C15.0196 5.10536 14.7652 5 14.5 5ZM14 13C14 13.1978 13.9414 13.3911 13.8315 13.5556C13.7216 13.72 13.5654 13.8482 13.3827 13.9239C13.2 13.9996 12.9989 14.0194 12.8049 13.9808C12.6109 13.9422 12.4327 13.847 12.2929 13.7071C12.153 13.5673 12.0578 13.3891 12.0192 13.1951C11.9806 13.0011 12.0004 12.8 12.0761 12.6173C12.1518 12.4346 12.28 12.2784 12.4444 12.1685C12.6089 12.0586 12.8022 12 13 12C13.2651 12.0004 13.5192 12.1059 13.7067 12.2933C13.8941 12.4808 13.9996 12.7349 14 13ZM9.5 3H7V4H9.5V7H6V8H7.5V11.071C7.02867 11.1927 6.61791 11.4821 6.3447 11.885C6.07149 12.2879 5.9546 12.7766 6.01594 13.2595C6.07728 13.7424 6.31263 14.1864 6.67788 14.5082C7.04314 14.83 7.51321 15.0075 8 15.0075C8.48679 15.0075 8.95686 14.83 9.32212 14.5082C9.68737 14.1864 9.92272 13.7424 9.98406 13.2595C10.0454 12.7766 9.92851 12.2879 9.6553 11.885C9.3821 11.4821 8.97133 11.1927 8.5 11.071V8H9.5C9.76522 8 10.0196 7.89464 10.2071 7.70711C10.3946 7.51957 10.5 7.26522 10.5 7V4C10.5 3.73478 10.3946 3.48043 10.2071 3.29289C10.0196 3.10536 9.76522 3 9.5 3ZM9 13C9 13.1978 8.94135 13.3911 8.83147 13.5556C8.72159 13.72 8.56541 13.8482 8.38268 13.9239C8.19996 13.9996 7.99889 14.0194 7.80491 13.9808C7.61093 13.9422 7.43275 13.847 7.29289 13.7071C7.15304 13.5673 7.0578 13.3891 7.01921 13.1951C6.98063 13.0011 7.00043 12.8 7.07612 12.6173C7.15181 12.4346 7.27998 12.2784 7.44443 12.1685C7.60888 12.0586 7.80222 12 8 12C8.26509 12.0004 8.51922 12.1059 8.70667 12.2933C8.89412 12.4808 8.9996 12.7349 9 13ZM4.5 1H1.5C1.23478 1 0.98043 1.10536 0.792893 1.29289C0.605357 1.48043 0.5 1.73478 0.5 2V5C0.5 5.26522 0.605357 5.51957 0.792893 5.70711C0.98043 5.89464 1.23478 6 1.5 6H2.5V11.071C2.02867 11.1927 1.61791 11.4821 1.3447 11.885C1.07149 12.2879 0.954603 12.7766 1.01594 13.2595C1.07728 13.7424 1.31263 14.1864 1.67788 14.5082C2.04314 14.83 2.51321 15.0075 3 15.0075C3.48679 15.0075 3.95686 14.83 4.32212 14.5082C4.68737 14.1864 4.92272 13.7424 4.98406 13.2595C5.0454 12.7766 4.92851 12.2879 4.6553 11.885C4.38209 11.4821 3.97133 11.1927 3.5 11.071V6H4.5C4.76522 6 5.01957 5.89464 5.20711 5.70711C5.39464 5.51957 5.5 5.26522 5.5 5V2C5.5 1.73478 5.39464 1.48043 5.20711 1.29289C5.01957 1.10536 4.76522 1 4.5 1ZM4 13C4 13.1978 3.94135 13.3911 3.83147 13.5556C3.72159 13.72 3.56541 13.8482 3.38268 13.9239C3.19996 13.9996 2.99889 14.0194 2.80491 13.9808C2.61093 13.9422 2.43275 13.847 2.29289 13.7071C2.15304 13.5673 2.0578 13.3891 2.01921 13.1951C1.98063 13.0011 2.00043 12.8 2.07612 12.6173C2.15181 12.4346 2.27998 12.2784 2.44443 12.1685C2.60888 12.0586 2.80222 12 3 12C3.26522 12 3.51957 12.1054 3.70711 12.2929C3.89464 12.4804 4 12.7348 4 13ZM1.5 5V2H4.5L4.501 5H1.5Z" fill="#6D6D6D"/>
                    </svg>
                    <span className="business-type-text">{companyData.businessType || 'Manufacture'}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="company-header-right">
              <div className="header-right-row">
                <div className="company-stats-box">
                  <span 
                    className="company-stat-value" 
                    onClick={() => {
                      setActiveTab('reviews');
                      setShowReviewsSection(true);
                      window.history.replaceState(null, '', `/supplier/${id}/profile?tab=reviews`);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="View reviews"
                  >
                    {companyData.reviews || 0} Reviews
                  </span>
                  <div className="company-stats-divider"></div>
                  <span 
                    className="company-stat-value"
                    onClick={() => {
                      setActiveTab('product');
                      setShowReviewsSection(false);
                      window.history.replaceState(null, '', `/supplier/${id}/products`);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="View products"
                  >
                    {companyData.products?.length || companyData._count?.products || 0} Products
                  </span>
                </div>
                <button 
                  className={`bookmark-btn ${isBookmarked ? 'active' : ''}`}
                  onClick={handleToggleBookmark}
                  title={isBookmarked ? 'Remove from favorites' : 'Add to favorites'}
                  disabled={bookmarkToggling}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 0H11.5C11.1022 0 10.7206 0.158035 10.4393 0.43934C10.158 0.720644 10 1.10218 10 1.5V18C10.0001 18.1338 10.036 18.2652 10.1039 18.3805C10.1719 18.4958 10.2695 18.5908 10.3866 18.6557C10.5037 18.7206 10.636 18.7529 10.7698 18.7494C10.9036 18.7458 11.034 18.7066 11.1475 18.6356L16.75 15.1341L22.3534 18.6356C22.4669 18.7063 22.5972 18.7454 22.7309 18.7488C22.8646 18.7522 22.9967 18.7198 23.1136 18.655C23.2306 18.5902 23.3281 18.4953 23.396 18.3801C23.4639 18.2649 23.4998 18.1337 23.5 18V1.5C23.5 1.10218 23.342 0.720644 23.0607 0.43934C22.7794 0.158035 22.3978 0 22 0ZM22 16.6472L17.1466 13.6144C17.0274 13.5399 16.8896 13.5004 16.7491 13.5004C16.6085 13.5004 16.4708 13.5399 16.3516 13.6144L11.5 16.6472V1.5H22V16.6472Z" fill={isBookmarked ? '#1F396C' : '#969696'}/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="company-tabs-wrapper">
            <div className="company-tabs">
              {tabs.map(tab => (
                <button 
                  key={tab.id}
                  className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab(tab.id);
                  setShowReviewsSection(tab.id === 'reviews');
                  // Update URL without full page reload
                  if (tab.id === 'home') {
                    window.history.replaceState(null, '', `/supplier/${id}`);
                  } else if (tab.id === 'company-profile') {
                    window.history.replaceState(null, '', `/supplier/${id}/profile`);
                  } else if (tab.id === 'product') {
                    window.history.replaceState(null, '', `/supplier/${id}/products`);
                  } else if (tab.id === 'reviews') {
                    window.history.replaceState(null, '', `/supplier/${id}/profile?tab=reviews`);
                  } else if (tab.id === 'contact') {
                    window.history.replaceState(null, '', `/supplier/${id}/contact`);
                  }
                }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          {activeTab === 'product' && (
            <div className="product-filter-row" ref={filterRef}>
              <div className="product-filter-wrapper">
                <button 
                  className={`product-filter-btn ${showProductFilter ? 'active' : ''} ${selectedCategory ? 'has-filter' : ''}`}
                  onClick={() => setShowProductFilter(!showProductFilter)}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" stroke="#1B1B1B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {showProductFilter && (
                  <div className="product-filter-dropdown">
                    <div className="filter-dropdown-header">Building Materials</div>
                    {productCategories.filter(c => c !== 'Building Materials').map(category => (
                      <button
                        key={category}
                        className={`filter-option ${selectedCategory === category ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedCategory(selectedCategory === category ? null : category);
                          setShowProductFilter(false);
                        }}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="company-main-content">

          {/* Reviews Section */}
          {activeTab === 'reviews' ? (
            <div className="reviews-section">
              <h2 className="reviews-title">Customer Reviews</h2>
              <div className="reviews-summary">
                <div className="reviews-count">
                  <span className="count-number">{companyData.sellerReviews?.length || companyData.reviews || 0}</span>
                  <span className="count-label">Total Reviews</span>
                </div>
                <div className="reviews-rating">
                  <div className="stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} width="24" height="24" viewBox="0 0 24 24" fill={star <= Math.round(companyData.avgRating || 0) ? "#FFB800" : "#D3D3D3"}>
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                      </svg>
                    ))}
                  </div>
                  <span className="rating-text">{(companyData.avgRating || 0).toFixed(1)} out of 5</span>
                </div>
              </div>
              
              <div className="reviews-list">
                {/* Show actual reviews if they exist */}
                {companyData.sellerReviews && companyData.sellerReviews.length > 0 ? (
                  companyData.sellerReviews.map((review) => (
                    <div key={review.id} className="review-card">
                      <div className="review-header">
                        <div className="reviewer-info">
                          <div className="reviewer-avatar">
                            {review.reviewerName?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="reviewer-details">
                            <span className="reviewer-name">{review.reviewerName}</span>
                            {review.reviewerCompany && (
                              <span className="reviewer-company">{review.reviewerCompany}</span>
                            )}
                          </div>
                        </div>
                        <div className="review-rating">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <svg key={star} width="16" height="16" viewBox="0 0 24 24" fill={star <= review.rating ? "#FFB800" : "#D3D3D3"}>
                              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                            </svg>
                          ))}
                        </div>
                      </div>
                      {review.title && <h4 className="review-title">{review.title}</h4>}
                      <p className="review-comment">{review.comment}</p>
                      <span className="review-date">
                        {new Date(review.createdAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="no-reviews-container">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#D3D3D3" strokeWidth="1.5">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                    </svg>
                    <p className="no-reviews">No reviews yet</p>
                    <p className="no-reviews-subtext">Be the first to review this supplier</p>
                  </div>
                )}
              </div>
            </div>
          ) : activeTab === 'product' ? (
            /* Product Tab Content - Figma Design */
            <div className="products-tab-content-figma">
              {/* Category Sidebar */}
              <div className="ptf-sidebar">
                <div className="ptf-category-list">
                  {sidebarCategories.length > 0 ? sidebarCategories.map((category) => (
                    <div key={category.id} className="ptf-category-section">
                      <div 
                        className="ptf-category-header"
                        onClick={() => {
                          handleCategorySelect(category.name);
                          if (category.subcategories) toggleCategory(category.id);
                        }}
                      >
                        <span className={`ptf-category-name ${selectedCategory === category.name ? 'active' : ''}`}>
                          {category.name}
                        </span>
                        <svg 
                          className={`ptf-category-arrow ${expandedCategories[category.id] ? 'expanded' : ''}`} 
                          width="24" height="24" viewBox="0 0 24 24" fill="none"
                        >
                          {category.subcategories ? (
                            <path d="M17 10L12 15L7 10" stroke={selectedCategory === category.name ? '#333' : '#6D6D6D'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          ) : (
                            <path d="M10 17L15 12L10 7" stroke={selectedCategory === category.name ? '#333' : '#6D6D6D'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          )}
                        </svg>
                      </div>
                      {category.subcategories && expandedCategories[category.id] && (
                        <div className="ptf-subcategory-list">
                          {category.subcategories.map((sub, idx) => (
                            <div 
                              key={idx} 
                              className={`ptf-subcategory-item ${selectedSubcategory === sub.name ? 'active' : ''}`}
                              onClick={() => handleSubcategorySelect(sub.name)}
                            >
                              {sub.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )) : (
                    <div className="ptf-no-categories">
                      <span className="ptf-category-name">No categories</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Products Grid Section */}
              <div className="ptf-products-section">
                <div className="ptf-products-grid">
                  {(() => {
                    const allProducts = (companyData?.products || []).filter(p => p.status === 'published');
                    // Filter by selected category
                    let filtered = allProducts;
                    if (selectedCategory) {
                      filtered = filtered.filter(product => {
                        const catName = product.category?.name || product.category;
                        return catName === selectedCategory;
                      });
                    }
                    // Filter by selected subcategory
                    if (selectedSubcategory) {
                      filtered = filtered.filter(product => product.subcategory?.name === selectedSubcategory);
                    }
                    const pageProducts = filtered.slice((productPage - 1) * PRODUCTS_PER_PAGE, productPage * PRODUCTS_PER_PAGE);

                    if (pageProducts.length === 0) {
                      return (
                        <div className="ptf-no-products" style={{gridColumn: '1 / -1', textAlign: 'center', padding: '60px 20px', color: '#6D6D6D', fontFamily: 'Poppins, sans-serif'}}>
                          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#D3D3D3" strokeWidth="1.5" style={{marginBottom: '16px'}}>
                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                            <line x1="12" y1="22.08" x2="12" y2="12"/>
                          </svg>
                          <p style={{fontSize: '18px', fontWeight: 500, color: '#333', margin: '0 0 8px'}}>No products found</p>
                          <p style={{fontSize: '14px', margin: 0}}>{selectedCategory ? `No products in "${selectedCategory}"${selectedSubcategory ? ` > "${selectedSubcategory}"` : ''}` : 'This supplier has not added any products yet.'}</p>
                        </div>
                      );
                    }

                    return pageProducts.map((product, index) => {
                      return (
                        <React.Fragment key={product.id || index}>
                          {/* Desktop: ProductCardFigma (same as homepage Featured Products) */}
                          <div className="ptf-desktop-card">
                            <ProductCardFigma 
                              product={{
                                ...product,
                                seller: {
                                  id: companyData.id,
                                  name: companyData.name,
                                  logo: companyData.logo,
                                  country: companyData.country,
                                  countryFlag: companyData.countryFlag,
                                  memberYears: companyData.memberYears,
                                  businessType: companyData.businessType,
                                  membershipPlan: companyData.membershipPlan,
                                  isVerified: companyData.isVerified,
                                  phone: companyData.phone || companyData.phoneNumber
                                }
                              }}
                              showInquiry={true}
                              showViewDetails={true}
                            />
                          </div>
                          {/* Mobile: Simple card */}
                          <div className="ptf-mobile-card">
                            <div className="ptf-product-image-wrapper">
                              <img 
                                src={product.image || product.images?.[0] || '/uploads/builder-images/328a0f6dd6e767b8279791bf791abd79bf7b4c61_w226.png'} 
                                alt={product.name || product.title} 
                                className="ptf-product-image"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/uploads/builder-images/328a0f6dd6e767b8279791bf791abd79bf7b4c61_w226.png';
                                }}
                              />
                            </div>
                            <div className="ptf-product-content">
                              <div className="ptf-product-details">
                                <p className="ptf-product-title">{product.name || product.title}</p>
                                <div className="ptf-product-pricing">
                                  <span className="ptf-price">{formatPriceRange(product.priceMin || 2.5, product.priceMax || 3.2)}</span>
                                  <span className="ptf-unit"> / {product.unit || 'Tons'}</span>
                                </div>
                                <div className="ptf-product-moq">
                                  <span className="ptf-moq-value">{product.moq || 15} {product.unit || 'Tons'}</span>
                                  <span className="ptf-moq-label"> (MOQ)</span>
                                </div>
                              </div>
                              <div className="ptf-product-actions">
                                <button className="ptf-btn-inquire" onClick={handleInquireNow}>Inquire Now</button>
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    });
                  })()}
                </div>

                {/* Pagination */}
                {(() => {
                  const allProducts = (companyData?.products || []).filter(p => p.status === 'published');
                  let filtered = allProducts;
                  if (selectedCategory) {
                    filtered = filtered.filter(product => (product.category?.name || product.category) === selectedCategory);
                  }
                  if (selectedSubcategory) {
                    filtered = filtered.filter(product => product.subcategory?.name === selectedSubcategory);
                  }
                  const totalPages = Math.ceil(filtered.length / PRODUCTS_PER_PAGE);
                  if (totalPages <= 1) return null;
                  return <Pagination currentPage={productPage} totalPages={totalPages} onPageChange={setProductPage} />;
                })()}
              </div>
            </div>
          ) : activeTab === 'contact' ? (
            /* Contact Tab Content - New Design */
            <div className="contact-tab-content">
              <div className="contact-section-new">
                {/* Contact Info Card */}
                <div className="contact-info-card-new">
                  {/* Contact Person */}
                  <div className="contact-person-box">
                    <img 
                      src={companyData.logo || "/uploads/builder-images/dab3e80e1404e8f3ea4dcc6e9c43cc98fe202f14_w160.png"} 
                      alt={companyData.contactPerson?.name || "Contact Person"} 
                      className="contact-person-avatar" 
                    />
                    <div className="contact-person-info">
                      <span className="contact-person-name">{companyData.contactPerson?.name || companyData.name || 'Contact Person'}</span>
                      <span className="contact-person-role">{companyData.contactPerson?.role || companyData.businessType || 'Sales Manager'}</span>
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="contact-separator-vertical"></div>

                  {/* Contact Details */}
                  <div className="contact-details-grid">
                    <div className="contact-labels-col">
                      <span>Telephone:</span>
                      <span>Address:</span>
                      <span>Homepage website:</span>
                      <span>City:</span>
                      <span>Country:</span>
                      <span>Zip/Postal Code:</span>
                    </div>
                    <div className="contact-values-col">
                      <span className="contact-phone-link">{getPhoneDisplay()}</span>
                      <span>{companyData.address?.trim() || 'N/A'}</span>
                      <span>{companyData.websiteLink?.trim() || 'N/A'}</span>
                      <span>{companyData.city?.trim() || 'N/A'}</span>
                      <span>{companyData.country?.trim() || 'N/A'}</span>
                      <span>{companyData.zipCode?.trim() || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Inquiry Form */}
                <div className="inquiry-form-card-new">
                  <h2 className="inquiry-title-new">Send a direct inquiry to this supplier</h2>
                  
                  <form onSubmit={handleInquirySubmit} className="inquiry-form-new">
                    <div className="form-recipient-row">
                      <span className="recipient-label">To:</span>
                      <span className="recipient-name">{companyData.name}</span>
                    </div>

                    <div className="form-group-new">
                      <label className="form-label-new">Message:</label>
                      <div className="textarea-wrapper-new">
                        <textarea
                          value={inquiryMessage}
                          onChange={(e) => setInquiryMessage(e.target.value)}
                          placeholder="enter product details"
                          className="form-textarea-new"
                          required
                        />
                        <div className="textarea-resize-icon">
                          <svg width="19" height="16" viewBox="0 0 20 17" fill="none">
                            <path d="M9.26709 16.4229L18.7925 10.9229" stroke="#6D6D6D"/>
                            <path d="M5.26709 14.4229L18.2671 6.42285" stroke="#6D6D6D"/>
                            <path d="M0.26709 12.4229L19.2671 0.422852" stroke="#6D6D6D"/>
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="form-group-new form-group-email-new">
                      <label className="form-label-new">Email:</label>
                      <div className="input-wrapper-new">
                        <input
                          type="email"
                          value={inquiryEmail}
                          onChange={(e) => setInquiryEmail(e.target.value)}
                          placeholder="Please enter your business email address"
                          className="form-input-new"
                          required
                        />
                      </div>
                    </div>

                    <button type="submit" className="submit-inquiry-btn" disabled={inquiryLoading}>
                      {inquiryLoading ? 'Sending...' : 'Send Inquire'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          ) : activeTab === 'company-profile' ? (
          /* Company Profile Tab Content - Figma Exact Match */
          <>
          {/* Content Grid - Same cover/description as Home */}
          <div className="content-grid">
            {/* Left Column - Video/Image */}
            <div className="content-left">
              {companyData.videoUrl ? (
                <div className="video-container">
                  <iframe 
                    src={companyData.videoUrl} 
                    title="Company Video"
                    frameBorder="0"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (companyData.coverImage || companyData.logo) ? (
                <div className="cover-image-container">
                  <img src={companyData.coverImage || companyData.logo} alt={companyData.name} className="cover-image" onError={(e) => { if (companyData.logo && e.target.src !== companyData.logo) { e.target.src = companyData.logo; } else { e.target.onerror = null; e.target.src = '/uploads/builder-images/561da124ca9a57c1461a9da91c830f61842922c8_w104.png'; }}} />
                </div>
              ) : (
                <div className="video-placeholder">
                  <div className="play-button">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="32" height="32" rx="16" fill="#0397DD" fillOpacity="0.2"/>
                      <path fillRule="evenodd" clipRule="evenodd" d="M16 28C17.5759 28 19.1363 27.6896 20.5922 27.0866C22.0481 26.4835 23.371 25.5996 24.4853 24.4853C25.5996 23.371 26.4835 22.0481 27.0866 20.5922C27.6896 19.1363 28 17.5759 28 16C28 14.4241 27.6896 12.8637 27.0866 11.4078C26.4835 9.95189 25.5996 8.62902 24.4853 7.51472C23.371 6.40042 22.0481 5.5165 20.5922 4.91345C19.1363 4.31039 17.5759 4 16 4C12.8174 4 9.76516 5.26428 7.51472 7.51472C5.26428 9.76515 4 12.8174 4 16C4 19.1826 5.26428 22.2348 7.51472 24.4853C9.76516 26.7357 12.8174 28 16 28ZM14.3773 10.6533L21.9027 14.8347C22.1104 14.9502 22.2835 15.1191 22.404 15.324C22.5245 15.5289 22.5881 15.7623 22.5881 16C22.5881 16.2377 22.5245 16.4711 22.404 16.676C22.2835 16.8809 22.1104 17.0498 21.9027 17.1653L14.3773 21.3467C14.1337 21.4821 13.8588 21.5515 13.5801 21.5481C13.3013 21.5446 13.0283 21.4683 12.7881 21.3269C12.5479 21.1854 12.3488 20.9836 12.2105 20.7416C12.0723 20.4995 11.9997 20.2254 12 19.9467V12.0533C11.9997 11.7746 12.0723 11.5005 12.2105 11.2585C12.3488 11.0164 12.5479 10.8146 12.7881 10.6731C13.0283 10.5317 13.3013 10.4554 13.5801 10.4519C13.8588 10.4485 14.1337 10.5179 14.3773 10.6533Z" fill="#1F396C"/>
                    </svg>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Company Details */}
            <div className="content-right">
              <div className="company-details-section">
                <h2 className="company-title">{companyData.name}</h2>
                <p className="company-tagline">{companyData.tagline || 'Trusted Cement Supplier with 25+ Years of Industry Experience'}</p>
                <p className="company-description">{stripHtml(companyData.description) || 'Al Noor Cement Ltd. is a leading manufacturer and distributor of high-quality cement products across Bangladesh. We proudly serve large infrastructure projects, residential contractors, and wholesale buyers with consistent quality and timely delivery.'}</p>
                
                <div className="contact-section">
                  <h3 className="section-title">Contact Us Now</h3>
                  <p className="contact-note">Have questions or ready to get started? Our team is here to help with quotes, product info, or partnership opportunities. Reach out today — let's build something great together.</p>
                </div>
                
                <div className="action-buttons">
                  <button className="btn-inquire" onClick={handleInquireNow}>Inquire Now</button>
                  <button className="btn-secondary" onClick={handleViewMobile}>
                    {showMobile ? getPhoneDisplay() : 'View mobile'}
                  </button>
                  <button className="btn-secondary" onClick={handleChatNow}>Chat Now</button>
                </div>
              </div>
              
              <div className="certificates-section">
                <h3 className="section-title">Certificates & Trust Marks</h3>
                <p className="certificates-note">
                  {companyData.name} is a leading {companyData.businessType || 'manufacturer'} and distributor of high-quality products. <span className="see-more" onClick={handleSeeMoreCertificates}>{showCertificatesDetails ? 'See less' : 'See more'}</span>
                </p>
                {showCertificatesDetails && (
                  <div className="certificates-details">
                    {companyData.certificates && companyData.certificates.length > 0 ? (
                      <ul className="certificates-list">
                        {parseJsonField(companyData.certificates).map((cert, index) => (
                          <li key={index} className="certificate-item">
                            {typeof cert === 'string' ? cert : cert.name || cert.title || 'Certificate'}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="no-certificates">ISO 9001:2015 Quality Management System certified. Additional certifications available upon request.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Basic Information Section */}
          <div className="cp-basic-info-section">
            <h3 className="cp-section-heading">Basic Information</h3>
            <div className="cp-info-table">
              <div className="cp-info-row">
                <span className="cp-label">Establish Year</span>
                <span className="cp-value">: {companyData.establishYear || companyData.yearEstablished || 'N/A'}</span>
              </div>
              <div className="cp-info-row">
                <span className="cp-label">License Number</span>
                <span className="cp-value">: {companyData.licenseNumber || 'N/A'}</span>
              </div>
              <div className="cp-info-row">
                <span className="cp-label">Service Area</span>
                <span className="cp-value">: {companyData.serviceArea || 'N/A'}</span>
              </div>
              <div className="cp-info-row">
                <span className="cp-label">Number of Employee</span>
                <span className="cp-value">: {companyData.numberOfEmployees || companyData.employees || 'N/A'}</span>
              </div>
              <div className="cp-info-row">
                <span className="cp-label">Category</span>
                <span className="cp-value">: {companyData.category || 'Construction'}</span>
              </div>
              <div className="cp-info-row">
                <span className="cp-label">Total Capitalization</span>
                <span className="cp-value">: {companyData.totalCapitalization || 'N/A'}</span>
              </div>
              <div className="cp-info-row">
                <span className="cp-label">Website Link</span>
                <span className="cp-value cp-value-link">: {companyData.websiteLink ? (
                  <a href={companyData.websiteLink.startsWith('http') ? companyData.websiteLink : `https://${companyData.websiteLink}`} target="_blank" rel="noopener noreferrer">
                    {companyData.websiteLink}
                  </a>
                ) : 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Company Overview Section */}
          <div className="cp-overview-section">
            <h3 className="cp-section-heading">Company Overview</h3>
            <p className="cp-overview-text">
              {stripHtml(companyData.description) || companyData.companyOverview || 'No company overview available.'}
            </p>
          </div>

          {/* Company Notes Section */}
          <div className="cp-notes-section">
            <h3 className="cp-section-heading">Company Notes</h3>
            {(() => {
              const notes = parseJsonField(companyData.companyNotes);
              if (notes && notes.length > 0) {
                return (
                  <>
                    <p className="cp-notes-text">
                      {notes.map(note => typeof note === 'string' ? note : note.text || '').join(' ')}
                    </p>
                    <div className="cp-notes-bullet-list">
                      {notes.map((note, index) => (
                        <p key={index} className="cp-notes-bullet-item">{typeof note === 'string' ? note : note.text || JSON.stringify(note)}</p>
                      ))}
                    </div>
                  </>
                );
              }
              return <p className="cp-notes-text">{typeof companyData.companyNotes === 'string' ? companyData.companyNotes : 'No company notes available.'}</p>;
            })()}
          </div>
          </>
          ) : activeTab === 'home' ? (
          /* Home Tab Content - Overview with all sections */
          <>

          {/* Product Highlight Section */}
          <div className="product-section-wrapper">
            <div className="product-section">
              <div className="section-header">
                <h2 className="section-heading">Product highlight</h2>
                <div className="carousel-controls">
                  <button className="carousel-btn carousel-prev" onClick={handleHighlightPrev}>
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <rect width="28" height="28" rx="14" fill="#DBDBDB" fillOpacity="0.8"/>
                      <path d="M17 20L11 14L17 8" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button className="carousel-btn carousel-next" onClick={handleHighlightNext}>
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <rect width="28" height="28" rx="14" fill="#1F396C"/>
                      <path d="M11 20L17 14L11 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="products-carousel" ref={highlightCarouselRef}>
                {((companyData?.products && companyData?.products.filter(p => p.status === 'published').length > 0) ? companyData.products.filter(p => p.status === 'published') : sampleProducts).map((product, index) => (
                  <ProductCardFigma 
                    key={product.id || index}
                    product={{
                      ...product,
                      seller: {
                        id: companyData.id,
                        name: companyData.name,
                        logo: companyData.logo,
                        country: companyData.country,
                        countryFlag: companyData.countryFlag,
                        memberYears: companyData.memberYears,
                        businessType: companyData.businessType,
                        membershipPlan: companyData.membershipPlan,
                        isVerified: companyData.isVerified
                      }
                    }}
                    showInquiry={true}
                    showViewDetails={true}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Company Snapshot Gallery Section - Always show */}
          <div className="product-section-wrapper">
            <div className="product-section">
              <div className="section-header">
                <h2 className="section-heading">Company Snapshot</h2>
                <div className="carousel-controls">
                  <button className="carousel-btn carousel-prev" onClick={handleSnapshotPrev}>
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <rect width="28" height="28" rx="14" fill="#DBDBDB" fillOpacity="0.8"/>
                      <path d="M17 20L11 14L17 8" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button className="carousel-btn carousel-next" onClick={handleSnapshotNext}>
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <rect width="28" height="28" rx="14" fill="#1F396C"/>
                      <path d="M11 20L17 14L11 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="snapshot-gallery" ref={snapshotCarouselRef}>
                  {snapshots.length > 0 ? snapshots.map((snapshot, index) => (
                    <div key={index} className="snapshot-gallery-item">
                      <img 
                        src={typeof snapshot === 'string' ? snapshot : (snapshot.url || snapshot.image || '/uploads/builder-images/74b65e113a43594db75517a423e27a2f181596bc_w416.png')} 
                        alt={`Snapshot ${index + 1}`} 
                        onError={(e) => { e.target.src = '/uploads/builder-images/74b65e113a43594db75517a423e27a2f181596bc_w416.png'; }}
                      />
                    </div>
                  )) : (
                    <p style={{color: '#6D6D6D', fontStyle: 'italic'}}>No snapshots available</p>
                  )}
                </div>
              </div>
            </div>

          {/* Brochure Section */}
          <div className="brochure-section">
            <h2 className="section-heading">Brochures</h2>
            <div className="brochure-content">
              {brochuresList && brochuresList.length > 0 ? (
                brochuresList.map((brochure, index) => (
                  <div className="brochure-item" key={index}>
                    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M55.2857 48.2588V5.88184C55.2857 4.32441 54.6665 2.83081 53.5641 1.72841C52.4617 0.626009 50.9681 0.00683594 49.4107 0.00683594H11.6786C10.1212 0.00683594 8.62756 0.626009 7.52516 1.72841C6.42276 2.83081 5.80359 4.32441 5.80359 5.88184V59.8882C5.80359 62.0641 6.66735 64.1507 8.20577 65.6891C9.7442 67.2275 11.8308 68.0913 14.0068 68.0913H70.125" stroke="#CC4B4C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M27.7637 8.88086C28.4117 8.88086 28.9813 9.71529 29.385 10.2296C29.7643 10.713 30.6244 11.7377 28.9054 18.975C30.6386 22.5544 33.0943 26.2007 35.4471 28.6976C37.1327 28.3929 38.583 28.2373 39.7646 28.2373C41.778 28.2373 42.9981 28.7066 43.4957 29.6734C43.9071 30.4732 43.7387 31.4079 42.9943 32.4506C42.2781 33.4522 41.2907 33.9819 40.14 33.9819C38.5766 33.9819 36.756 32.9944 34.7258 31.044C31.0783 31.8064 26.8187 33.1667 23.3756 34.6723C22.3007 36.9532 21.2709 38.7905 20.3117 40.1379C18.9939 41.9829 17.8573 42.8443 16.7323 42.8443ZM20.1549 36.2537C17.4073 37.7979 16.2874 39.0669 16.2064 39.7817C16.1936 39.9 16.1589 40.2112 16.6193 40.509C16.7066 40.5655 17.3051 40.509 17.6714 40.2881C18.3561 39.8567 19.1611 38.9949 20.1549 36.2537ZM26.4459 22.08C27.1213 25.3837 25.9735 28.6306 24.6459 31.8052C27.3676 30.7507 30.1581 29.883 32.9978 29.208C31.2621 27.192 29.5277 24.6746 28.0838 22.08H26.4459ZM27.3034 11.2003C27.1774 11.2427 25.5934 13.4593 27.4268 15.3351C28.647 12.6159 27.3587 11.1823 27.3034 11.2003ZM53.4047 71.9992H1.881C1.38213 71.9992 0.903688 71.801 0.550932 71.4483C0.198176 71.0955 0 70.6171 0 70.1182V50.142H55.2857V70.1182C55.2857 71.157 54.4436 71.9992 53.4047 71.9992Z" fill="#CC4B4C"/>
                      <path d="M13.9946 68.1424H11.8848V55.1875H15.6108C16.1611 55.1875 16.7062 55.2749 17.2449 55.4511C17.7836 55.6272 18.2671 55.8908 18.6952 56.2418C19.1233 56.5928 19.4692 57.0184 19.7328 57.5186C19.9964 58.0188 20.1282 58.58 20.1282 59.2021C20.1282 59.8243 19.9964 60.3854 19.7328 60.8856C19.4692 61.3859 19.1233 61.8114 18.6952 62.1624C18.2671 62.5134 17.7836 62.777 17.2449 62.9532C16.7062 63.1293 16.1611 63.2174 15.6108 63.2174H13.9946V68.1424Z" fill="white"/>
                      <path d="M29.6344 68.1424H23.9609V55.1875H29.6344C30.4109 55.1875 31.1374 55.3233 31.8139 55.595C32.4905 55.8667 33.0824 56.2418 33.5898 56.7203C34.0972 57.1987 34.4915 57.7617 34.7727 58.4093C35.0538 59.0568 35.1944 59.7563 35.1944 60.5078V62.8221C35.1944 63.5736 35.0538 64.2731 34.7727 64.9207C34.4915 65.5682 34.0972 66.1312 33.5898 66.6096C33.0824 67.0881 32.4905 67.4632 31.8139 67.7349C31.1374 68.0066 30.4109 68.1424 29.6344 68.1424Z" fill="white"/>
                      <path d="M44.2282 56.7869V60.8652H43.6844V62.3065H38.2702V68.1424H36.1256V55.1875H44.2282V56.7869H38.2702Z" fill="white"/>
                    </svg>
                    <span className="brochure-name">Brochure {index + 1}</span>
                    <button className="btn-download-pdf" onClick={() => handleDownloadBrochure(brochure)}>Download PDF</button>
                  </div>
                ))
              ) : (
                <p className="no-brochures">N/A</p>
              )}
            </div>
          </div>
        </>
        ) : null}
        </div>
      </main>
      
      <Footer />

      {/* Chat Modal */}
      <ChatModal 
        isOpen={isChatModalOpen}
        onClose={() => setIsChatModalOpen(false)}
        seller={{
          id: companyData?.userId || companyData?.id,
          name: companyData?.name || companyData?.companyName,
          logo: companyData?.logo,
          isVerified: companyData?.isVerified
        }}
        contactPerson={getContactPerson()}
      />

      {/* Upgrade Prompt Modal for features */}
      <UpgradePromptModal
        isOpen={showUpgradeModal && !upgradeRequired}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature || 'Premium Features'}
        currentPlan={planDisplayName || 'Basic'}
        requiredPlan="GCC Business"
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />

      {bookmarkToast && (
        <div className={`fav-toast fav-toast--${bookmarkToast.type}`}>{bookmarkToast.msg}</div>
      )}
    </div>
  );
};

export default CompanyProfilePage;
